// Package imports:
import React, { useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
// Style imports:
// import './HomeBookmarks.scss';
// Component imports:
import EditBookmarkModal from './EditBookmarkModal';
import DeleteBookmarkModal from './DeleteBookmarkModal';
import StickyHeaderTable from '../common/StickyHeaderTable';
import SortFilterTable from '../common/SortFilterTable';
// Action imports:
import { requestBookmarks } from '../../redux-store/actions/bookmarkActions';
// Service imports:
import { formatDate, getUniqueTickId, turnDateToSimpleTime } from '../../services/utils';
// Type imports:
import { RootState } from '../../redux-store/reducers';
import { BookmarkTickData, IBookmark } from '../../types/api/BookmarkTypes';
import { timestampObjectCompareFunction } from '../../services/sortUtils';

type ReduxProps = ConnectedProps<typeof connector>;
interface IOwnProps {
    showDateInTimestamp: boolean
    reverseSort: boolean,
    currentTradingDay?: string
}
type Props = ReduxProps & IOwnProps;

const HomeBookmarks: React.FC<Props> = ({
    showDateInTimestamp,
    reverseSort,
    currentTradingDay,
    bookmarks,
    bookmarkError,
    accessTokenV2,
    requestBookmarks
}) => {
    const [ editBookmark, setEditBookmark ] = useState<IBookmark | null>(null);
    const [ deleteBookmark, setDeleteBookmark ] = useState<IBookmark | null>(null);

    useEffect(() => {
        if ((bookmarks === null) && accessTokenV2) {
            requestBookmarks(accessTokenV2);
        }
        // eslint-disable-next-line
    }, [bookmarks, accessTokenV2, requestBookmarks]);

    const { bookmarkTickData, isLoading, isError } = useMemo(() => {
        const data: {
            bookmarkTickData: BookmarkTickData[]
            isLoading: boolean
            isError: boolean
        } = {
            bookmarkTickData: [],
            isLoading: false,
            isError: false
        };

        if (bookmarks === null || bookmarks === 'waiting') {
            data.isLoading = true;
            return data;
        }

        if (bookmarkError !== null) {
            data.isError = true;
            return data;
        }

        const filteredBookmarksForDate = (currentTradingDay === undefined)
            ? bookmarks
            : bookmarks.filter(bookmark => turnDateToSimpleTime(new Date(bookmark.timestamp)) === turnDateToSimpleTime(new Date(currentTradingDay)));

        const sortedBookmarks = [...filteredBookmarksForDate].sort((bookmark1, bookmark2) => {
            if (new Date(bookmark1.timestamp) < new Date(bookmark2.timestamp)) return (reverseSort) ? 1 : -1;
            if (new Date(bookmark1.timestamp) > new Date(bookmark2.timestamp)) return (reverseSort) ? -1 : 1;
            return 0;
        });

        data.bookmarkTickData = sortedBookmarks;
        return data;
    }, [ bookmarks, bookmarkError, currentTradingDay, reverseSort ]);

    // ============================ Render Functions ============================
    // --------------- Render Symbol Body ---------------
    const RenderSumbolBody = ({bookmark}: { bookmark: BookmarkTickData; }) => {
        return (
            <a
                href={`/sigti/${bookmark.symbol}?tradingDay=${turnDateToSimpleTime(new Date(bookmark.timestamp))}#${getUniqueTickId(bookmark)}`}
            >
                {bookmark.symbol}
            </a>
        )
    }
    // --------------- Render Actions Body ---------------
    const RenderActionsBody = ({bookmark}: { bookmark: BookmarkTickData; }) => {
        return (
            <>
                <FontAwesomeIcon
                    icon={faEdit}
                    className='BookmarkIcon'
                    onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        setEditBookmark(bookmark);
                    }}
                />
                <FontAwesomeIcon
                    icon={faTrashAlt}
                    className='BookmarkIcon'
                    onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        setDeleteBookmark(bookmark);
                    }}
                />
            </>
        )
    }

    return (
        <div id='HomeBookmarks'>
            <h5 className='SectionTitle'>Bookmarks</h5>
            <div className='TableWrapper'>
                <StickyHeaderTable
                    hideScrollbar={false}
                >
                    <SortFilterTable
                        data={bookmarkTickData}
                        isLoading={isLoading}
                        isError={isError}
                        columns={[{
                            title: 'Symbol',
                            dataAccessor: (bookmark) => <RenderSumbolBody bookmark={bookmark} />,
                            textAlign: 'left',
                            simpleSortable: ({symbol}) => symbol,
                            sortTiebreaker: timestampObjectCompareFunction
                        }, {
                            title: 'Timestamp',
                            textAlign: 'left',
                            dataAccessor: ({timestamp}) => formatDate(timestamp, !showDateInTimestamp),
                            sortable: timestampObjectCompareFunction
                        }, {
                            title: 'Comment',
                            dataAccessor: ({bmcomment}) => bmcomment,
                            textAlign: 'left',
                            simpleSortable: ({bmcomment}) => bmcomment,
                            sortTiebreaker: timestampObjectCompareFunction
                        }, {
                            title: 'User',
                            dataAccessor: ({username}) => username,
                            textAlign: 'left',
                            simpleSortable: ({username}) => username,
                            sortTiebreaker: timestampObjectCompareFunction
                        }, {
                            title: 'Actions',
                            dataAccessor: (bookmark) => <RenderActionsBody bookmark={bookmark} />,
                        }]}
                    />
                </StickyHeaderTable>
            </div>
            {
                (editBookmark !== null) ? ( // if
                    <EditBookmarkModal
                        bookmark={editBookmark}
                        onHide={() => setEditBookmark(null)}
                    />
                ) : (deleteBookmark !== null) ? ( // else if
                    <DeleteBookmarkModal
                        bookmark={deleteBookmark}
                        onHide={() => setDeleteBookmark(null)}
                    />
                ) : null
            }
        </div>
    );
}

function mapStateToProps({ bookmarkReducer, loginReducer }: RootState) {
    const { bookmarks, bookmarkError } = bookmarkReducer;
    const { accessTokenV2 } = loginReducer;
    return {
        bookmarks,
        bookmarkError,
        accessTokenV2,
    }
}

const connector = connect(mapStateToProps, { requestBookmarks });
export default connector(HomeBookmarks);